import { DateFormatter } from '@repo/toolkit/date'
import DataTable, { Column } from '@repo/ui/data-table'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getTripFolderOlArrivals } from '../../../api/trip-folder/get-trip-folder-ol-arrivals'
import { TripFolderOlArrival } from '../../../lib/types/models/trip-folder/trip-folder'
import { IdInput, Time } from '../../../lib/types/utils'
import { TripFolderTimezone } from '../../../views/trip-folder/trip-folder-details/folder-details'

type Props = {
  folderId: number
  activeTimezone: TripFolderTimezone
}

const TripFolderOlArrivalsSection: React.FC<Props> = ({ folderId, activeTimezone }) => {
  const { data, isPending } = useQuery({
    queryKey: ['trip-folder-ol-arrivals', folderId],
    queryFn: () => getTripFolderOlArrivals(folderId)
  })

  const handleConvertTimeZone = (
    time: Time | string | undefined,
    timezone?: string,
    date?: string
  ) => {
    if (!timezone || !time) return DateFormatter.getFormattedTime(time)

    return DateFormatter.convertTimeZone(
      time,
      timezone,
      activeTimezone === 'Local' ? timezone : activeTimezone,
      date ? new Date(date) : undefined
    )
  }

  const columns: Column<IdInput<TripFolderOlArrival>>[] = [
    {
      header: 'Centre',
      accessorKey: 'center.name',
      cell: ({ row }) => row.original.center?.name ?? '-'
    },
    {
      header: 'Lieu de RDV prestataire étranger',
      accessorKey: 'appointmentPlace.label',
      cell: ({ row }) => row.original.appointmentPlace.label
    },
    {
      header: 'Heure de RDV prestataire étranger',
      accessorKey: 'appointmentTime',
      cell: ({ row }) =>
        handleConvertTimeZone(
          row.original.appointmentTime,
          row.original.appointmentTimeZone,
          row.original.appointmentDate
        )
    },
    {
      header: 'Consignes pour le prestataire étranger',
      accessorKey: 'guidelineContent',
      cell: ({ row }) => <div className="whitespace-pre-wrap">{row.original.guidelineContent}</div>
    },
    {
      header: 'Coach company name',
      accessorKey: 'coachCompany.name',
      cell: ({ row }) => row.original.coachCompany?.name ?? '-'
    },
    {
      header: 'Coach company n° tel',
      accessorKey: 'coachCompany.phoneNumber',
      cell: ({ row }) => row.original.coachCompany?.phoneNumber ?? '-'
    },
    {
      header: 'Coach company n° tel emergency',
      accessorKey: 'coachCompany.emergencyNumber',
      cell: ({ row }) => row.original.coachCompany?.emergencyNumber ?? '-'
    }
  ]

  if (isPending) return <Skeleton className={'h-[300px] w-full'} />

  if (!isPending && (!data || data?.length === 0)) {
    return (
      <div className="flex justify-center items-center p-8 bg-gray-50 rounded-3xl border border-primary-light">
        <Typography variant={'secondary-body'}>Aucun transfert OL arrivé</Typography>
      </div>
    )
  }

  return (
    <DataTable<IdInput<TripFolderOlArrival>>
      columns={columns}
      rowCount={data.length}
      paginated={false}
      data={data.map((arrival, index) => ({
        ...arrival,
        id: index
      }))}
    />
  )
}

export default TripFolderOlArrivalsSection
